<template>
	<v-dialog :model-value="isOpen" width="600" persistent>
		<v-card>
			<v-card-title class="text-h1">
				<v-icon icon="mdi-content-copy"/>
				Copy Playlist
			</v-card-title>
			<v-card-text>
				<div class="mb-2">
					<span class="text-h2 text-secondary">
						Copy From Here
						<v-icon icon="mdi-tray-arrow-up"/>
					</span>
					<v-spacer/>
					<v-divider opacity="1" thickness="3" color="secondary"/>
				</div>
				<div class="d-flex">
					<div class="w-100">
						<v-label class="text-caption">Source Station</v-label>
						<v-text-field :model-value="sourceStation.StationName"
							class="w-100"
							placeholder="Source Station"
							variant="outlined"
							density="compact"
							readonly
							hide-details
						/>
					</div>
					<div class="w-100 mx-3">
						<v-label class="text-caption">Copy Start Date</v-label>
						<v-menu v-model="isMenuOpen.source.date"
							:close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-text-field v-bind="props"
									:model-value="copyDateTime.source.date.format('YYYY-MM-DD')"
									variant="outlined"
									density="compact"
									append-inner-icon="mdi-calendar"
									readonly
									hide-details/>
							</template>
							<template v-slot:default>
								<v-card>
									<v-date-picker v-model="copyDateTime.source.date"
										show-adjacent-months
										hide-header
										hide-details
										@update:model-value="isMenuOpen.source.date=false"
									/>
								</v-card>
							</template>
						</v-menu>
					</div>
					<div class="w-100 mr-3">
						<v-label class="text-caption">Copy Start Time</v-label>
						<v-menu v-model="isMenuOpen.source.time"
							:close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-text-field v-bind="props"
									:model-value="copyDateTime.source.time.format('HH:mm')"
									variant="outlined"
									density="compact"
									append-inner-icon="mdi-clock-time-four-outline"
									flat
									hide-details
									readonly/>
							</template>
							<template v-slot:default>
								<v-card>
									<v-time-picker :model-value="copyDateTime.source.time.format('HH:mm')"
										format="24hr"
										border="sm"
										color="secondary"
										elevation="6"
										:allowed-minutes="[0]"
										hide-header
										hide-details
										@update:model-value="updateTimeModelValue('source', $event)"
									/>
								</v-card>
							</template>
						</v-menu>
					</div>
					<div class="w-100">
						<v-label class="text-caption">Number of hours to copy</v-label>
						<v-text-field v-model="numberOfHoursToCopy"
							type="number"
							variant="outlined"
							density="compact"
							hide-details
						/>
					</div>
				</div>
				<div class="mt-3 mb-2">
					<span class="text-h2 text-secondary">
						Copy To Here
						<v-icon icon="mdi-tray-arrow-up"/>
					</span>
					<v-spacer/>
					<v-divider opacity="1" thickness="3" color="secondary"/>
				</div>
				<div class="d-flex">
					<div class="w-100">
						<v-label class="text-caption">Target Station</v-label>
						<v-select v-model="copyTargetStations"
							:items="favoriteStations.concat(filteredStations)"
							item-title="name"
							item-value="StationID"
							id="copy-playlist-menu-container"
							variant="outlined"
							density="compact"
							return-object
							multiple
							hide-details
							@click="focusOnSelectorInput">
							<template v-slot:prepend-item>
								<v-list-item height="38">
									<template v-slot:default>
										<v-text-field id="copy-station-selector-input"
											v-model="stationSearchTerm"
											@update:model-value="loadStations()"
											@update:focused="updateFocus"
											variant="outlined"
											density="compact"
											clearable
											single-line
											hide-details
											append-inner-icon="mdi-magnify"
											label="Filter"/>
									</template>
								</v-list-item>
							</template>
							<template v-slot:no-data>
								<v-list-item disabled>
									<v-list-item-title class="text-disabled text-body-1">
										<div v-if="stationSearchTerm && stationSearchTerm.length > 1">
											<div v-if="stations.loading">
												<v-progress-circular indeterminate
													size="16" width="2" class="mr-1 mb-1"/>
												Loading stations...
											</div>
											<div v-else>
												No stations found.
											</div>
										</div>
									</v-list-item-title>
								</v-list-item>
							</template>
							<template v-slot:append-item v-if="hasMore">
								<div v-intersect="loadMore">
									<v-list-item>
										<v-list-item-title class="d-inline-flex text-disabled text-body-1">
											<v-progress-circular indeterminate color="primary" size="16" width="2"
												class="mx-2 my-1"/>
											Loading more stations...
										</v-list-item-title>
									</v-list-item>
								</div>
							</template>
						</v-select>
					</div>
					<div class="w-100 mx-3">
						<v-label class="text-caption"> Target Starting Date</v-label>
						<v-menu v-model="isMenuOpen.target.date"
							:close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-text-field v-bind="props"
									:model-value="copyDateTime.target.date.format('YYYY-MM-DD')"
									variant="outlined"
									density="compact"
									append-inner-icon="mdi-calendar"
									readonly
									hide-details/>
							</template>
							<template v-slot:default>
								<v-card>
									<v-date-picker v-model="copyDateTime.target.date"
										show-adjacent-months
										hide-header
										hide-details
										@update:model-value="isMenuOpen.target.date=false"
									/>
								</v-card>
							</template>
						</v-menu>
					</div>
					<div class="w-100 mr-3">
						<v-label class="text-caption">Copy Start Time</v-label>
						<v-menu v-model="isMenuOpen.target.time"
							:close-on-content-click="false">
							<template v-slot:activator="{ props }">
								<v-text-field v-bind="props"
									:model-value="copyDateTime.target.time.format('HH:mm')"
									variant="outlined"
									density="compact"
									append-inner-icon="mdi-clock-time-four-outline"
									flat
									hide-details
									readonly/>
							</template>
							<template v-slot:default>
								<v-card>
									<v-time-picker :model-value="copyDateTime.target.time.format('HH:mm')"
										format="24hr"
										border="sm"
										color="secondary"
										elevation="6"
										:allowed-minutes="[0]"
										hide-header
										hide-details
										@update:model-value="updateTimeModelValue('target', $event)"
									/>
								</v-card>
							</template>
						</v-menu>
					</div>
				</div>
			</v-card-text>
			<v-card-actions class="text-right">
				<v-btn color="primary" @click="copyPlaylist"> Copy</v-btn>
				<v-btn @click="$emit('close')"> Close</v-btn>
			</v-card-actions>
		</v-card>
	</v-dialog>

</template>

<script>
/* Libraries */
import moment from "moment-timezone";
import {uuid} from "vue-uuid";
import store from "@/store";

/* Modules */
import StationSelector from "@/components/StationSelector.vue";

/* Mixins */
import StationSelectionMixin from "@/mixins/StationSelectionMixin";

export default {
	name: "CopyPlaylistDialog",

	components: {StationSelector},

	mixins: [StationSelectionMixin],

	props: ["isOpen", "sourceStation"],

	data() {
		return {
			isMenuOpen: {
				source: {
					date: false,
					time: false
				},
				target: {
					date: false,
					time: false
				}
			},
			copyDateTime: {
				source: {
					date: moment.tz(this.sourceStation?.StationTimeZone).startOf("hour"),
					time: moment.tz(this.sourceStation?.StationTimeZone).startOf("hour")
				},
				target: {
					date: moment.tz(this.sourceStation?.StationTimeZone).startOf("hour"),
					time: moment.tz(this.sourceStation?.StationTimeZone).startOf("hour")
				}
			},
			numberOfHoursToCopy: 1,
			copyTargetStations: []
		}
	},

	computed: {
		orgID() {
			return this.$store.getters.getOrgID();
		}
	},

	methods: {
		copyPlaylist() {
			const sourceHour = this.copyDateTime.source.time.hour();
			const targetHour = this.copyDateTime.target.time.hour();

			const sourceMoment = this.copyDateTime.source.date.hour(sourceHour);
			const targetMoment = this.copyDateTime.target.date.hour(targetHour);

			const playlistToCopy = this.$store.getters.getPlaylistByDateTime(
				this.sourceStation.StationID,
				sourceMoment
			);

			if (playlistToCopy) {
				this.copyTargetStations.forEach(station => {
					let targetDateTime = moment.tz(targetMoment.format("YYYY-MM-DD HH:mm"), station.timezone);
					const jobKey = uuid.v1();
					const payload = {
						OrganizationID: this.orgID,
						StationID: station.StationID,
						JobKey: jobKey,
						JobName: `UI_CopyPL${playlistToCopy.PlaylistID}`,
						JobType: "CopyPlaylist",
						JobRequestData: {
							Username: store.getters.getUserFullName(),
							targetStationID: station.StationID,
							targetStationName: station.name,
							sourceStationID: this.sourceStation.StationID,
							sourceStationName: this.sourceStation.StationName,
							fromDateTime: sourceMoment,
							toDateTime: targetDateTime,
							hours: this.numberOfHoursToCopy
						}
					};
					this.$store.dispatch("runJob", payload).then(response => {
						this.$store.commit("addJob", {
							JobKey: response.JobKey,
							JobTime: response.CreatedDateTime,
							JobMessage: `Copying playlist from ${this.sourceStation.StationName} to ${station.name}`
						});
					})
				})
			}
			else {
				this.$store.commit("SHOW_NOTIFICATION_WITH_TYPE", {
					message: "No playlist to copy for this date."
				});
			}
			this.$emit("close");
		},

		focusOnSelectorInput() {
			this.$nextTick(() => {
				document.getElementById("copy-station-selector-input").focus();
			});
			this.loadStations();
		},

		updateFocus(focused) {
			if (!focused) {
				this.closeSelector();
			}
		},

		updateTimeModelValue(field, value) {
			this.isMenuOpen[field].time = false
			const [hour] = value.split(":");
			this.copyDateTime[field].time.hour(hour);
		},

		closeSelector() {
			this.$nextTick(() => {
				document.getElementById("copy-playlist-menu-container").blur();
			});
		},
	}

}
</script>

<style scoped lang="scss">

</style>
